import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, ProbationBreach, ProbationCase, ProbationCheckIn } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const PROBATION_ROLES = ['PROBATION_OFFICER'];

/** Prisons — the probation caseload (M07): community supervision, geo-stamped check-ins, and breaches. */
export function Probation() {
  const { persona } = useAuth();
  const isProbationOfficer = persona ? persona.roles.some((r) => PROBATION_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m07/probation-cases?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<ProbationCase>>(path);

  return (
    <div>
      <PageHeader
        title="Probation Caseload"
        subtitle="Community-supervision cases — mobile geo-stamped check-ins and breach logging (M07)."
        actions={
          <div className="flex items-center gap-2">
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
              <option value="REVOKED">Revoked</option>
            </select>
            {isProbationOfficer ? (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + Open case
              </button>
            ) : null}
          </div>
        }
      />

      {!isProbationOfficer ? <RoleNotice roles={PROBATION_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No probation cases match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Order source</th>
                <th className="table-head">Order ref</th>
                <th className="table-head">Started</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.nin}</td>
                  <td className="table-cell text-xs text-slate-500">{c.orderSource.replaceAll('_', ' ')}</td>
                  <td className="table-cell font-mono text-xs">{c.orderRef}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(c.startDate)}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <CaseDetailModal
          id={selectedId}
          canAct={isProbationOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateCaseModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CaseDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const caseQ = useApiQuery<ProbationCase>(`/api/v1/m07/probation-cases/${id}`);
  const checkInsQ = useApiQuery<Page<ProbationCheckIn>>(`/api/v1/m07/probation-cases/${id}/check-ins?limit=20`);
  const c = caseQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBreach, setShowBreach] = useState(false);
  const [breachDescription, setBreachDescription] = useState('');
  const [breachSeverity, setBreachSeverity] = useState<'MINOR' | 'SERIOUS'>('MINOR');
  // m07-prisons has no GET for a case's breaches — only create + resolve-by-id
  // endpoints exist, so a just-logged breach is only actionable in this same
  // session, kept here rather than fetched.
  const [loggedBreach, setLoggedBreach] = useState<ProbationBreach | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const logBreach = async () => {
    setBusy(true);
    setError(null);
    try {
      const breach = await api.post<ProbationBreach>(`/api/v1/m07/probation-cases/${id}/breaches`, {
        description: breachDescription.trim(),
        severity: breachSeverity,
      });
      setLoggedBreach(breach);
      setShowBreach(false);
      setBreachDescription('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log breach');
    } finally {
      setBusy(false);
    }
  };

  const resolveBreach = (resolution: 'WARNING_ISSUED' | 'REVOKED' | 'DISMISSED') =>
    call(async () => {
      await api.post(`/api/v1/m07/probation-cases/${id}/breaches/${loggedBreach!.id}/resolve`, { resolution });
      setLoggedBreach(null);
    });

  return (
    <Modal title={c ? `Probation case — ${c.nin}` : 'Probation case'} onClose={onClose} wide>
      {caseQ.error ? <ErrorNote message={caseQ.error} /> : null}
      {caseQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={c.status} />} />
            <Detail label="Order source" value={c.orderSource.replaceAll('_', ' ')} />
            <Detail label="Order ref" value={c.orderRef} mono />
            <Detail label="Supervisor" value={c.supervisorId} mono />
            <Detail label="Started" value={formatDate(c.startDate)} />
            <Detail label="Planned end" value={c.endDate ? formatDate(c.endDate) : '—'} />
            {c.actualEndDate ? <Detail label="Actual end" value={formatDate(c.actualEndDate)} /> : null}
          </div>
          <div>
            <div className="field-label">Conditions</div>
            <ul className="list-inside list-disc text-sm text-slate-700">
              {c.conditions.map((cond, i) => (
                <li key={i}>{cond}</li>
              ))}
            </ul>
          </div>

          <div>
            <div className="field-label">Recent check-ins</div>
            {checkInsQ.error ? <ErrorNote message={checkInsQ.error} /> : null}
            {checkInsQ.loading ? (
              <Spinner />
            ) : (checkInsQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-400">No check-ins recorded yet.</p>
            ) : (
              <div className="max-h-40 space-y-1 overflow-y-auto text-sm">
                {checkInsQ.data!.items.map((ci) => (
                  <div key={ci.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5">
                    <span>{formatDateTime(ci.checkedAt)}</span>
                    <span className="font-mono text-xs text-slate-400">
                      {ci.lat.toFixed(3)}, {ci.lng.toFixed(3)}
                    </span>
                    <StatusBadge status={ci.withinRegion ? 'APPROVED' : 'WARN'} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {canAct && c.status === 'ACTIVE' ? (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m07/probation-cases/${id}/complete`))}>
                Mark completed
              </button>
              {!showBreach ? (
                <button className="btn-danger" onClick={() => setShowBreach(true)}>
                  Log a breach
                </button>
              ) : null}
            </div>
          ) : null}

          {showBreach ? (
            <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
              <Field label="Description">
                <textarea className="field-input" rows={2} value={breachDescription} onChange={(e) => setBreachDescription(e.target.value)} />
              </Field>
              <Field label="Severity">
                <select className="field-input" value={breachSeverity} onChange={(e) => setBreachSeverity(e.target.value as 'MINOR' | 'SERIOUS')}>
                  <option value="MINOR">Minor</option>
                  <option value="SERIOUS">Serious</option>
                </select>
              </Field>
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={() => setShowBreach(false)}>
                  Cancel
                </button>
                <button className="btn-danger" disabled={busy || breachDescription.trim().length < 2} onClick={logBreach}>
                  {busy ? 'Logging…' : 'Log breach'}
                </button>
              </div>
            </div>
          ) : null}

          {loggedBreach ? (
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-sm font-semibold text-amber-800">
                Breach logged ({loggedBreach.severity}): {loggedBreach.description}
              </div>
              <p className="text-xs text-amber-700">Resolve now — this module has no later lookup for an unresolved breach.</p>
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary" disabled={busy} onClick={() => resolveBreach('WARNING_ISSUED')}>
                  Issue warning
                </button>
                <button className="btn-secondary" disabled={busy} onClick={() => resolveBreach('DISMISSED')}>
                  Dismiss
                </button>
                <button className="btn-danger" disabled={busy} onClick={() => resolveBreach('REVOKED')}>
                  Revoke supervision
                </button>
              </div>
            </div>
          ) : null}

          {!canAct ? <RoleNotice roles={PROBATION_ROLES} /> : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateCaseModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [nin, setNin] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [orderSource, setOrderSource] = useState<'M37_COURT_ORDER' | 'PAROLE_BOARD'>('M37_COURT_ORDER');
  const [conditions, setConditions] = useState('Report to the probation office monthly, Remain within Seychelles');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m07/probation-cases', {
        nin: nin.trim(),
        orderRef: orderRef.trim(),
        orderSource,
        conditions: conditions
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Open a probation case" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="NIN">
          <input className="field-input font-mono" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. 108-4471-2-2-19" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Order source">
            <select className="field-input" value={orderSource} onChange={(e) => setOrderSource(e.target.value as typeof orderSource)}>
              <option value="M37_COURT_ORDER">M37 court order</option>
              <option value="PAROLE_BOARD">Parole board</option>
            </select>
          </Field>
          <Field label="Order reference">
            <input className="field-input" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} />
          </Field>
        </div>
        <Field label="Conditions (comma separated)">
          <textarea className="field-input" rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !nin.trim() || !orderRef.trim() || conditions.trim().length < 2} onClick={submit}>
            {busy ? 'Opening…' : 'Open case'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
